import api from '../config/api';

// CSV imports and exports (phase 4 §6). Files travel as JSON (writes are
// JSON-only): the bytes in base64, so the server reads the encoding itself.

/** The API's own address prefix ('' in development, '/backend' in production): for links the browser follows. */
export const apiBase = () => api.defaults.baseURL || '';

/** A File's bytes as base64 (the browser decodes nothing: Windows-1252 stays readable). */
export const fileToBase64 = async (file) => {
  const buffer = file.arrayBuffer ? await file.arrayBuffer() : await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return window.btoa(binary);
};

export const MAX_FILE_BYTES = 8 * 1024 * 1024;

export const createImport = async (inventoryId, typeId, file, encoding = 'auto') => (await api.post(`/inventories/${inventoryId}/imports`, {
  item_type_id: typeId, content_base64: await fileToBase64(file), filename: file.name, ...(encoding !== 'auto' ? { encoding } : {}),
})).data;
export const getProposal = async (importId, format) => (await api.get(`/imports/${importId}/proposal`, { params: { format } })).data;
export const saveMapping = async (importId, format, mapping) => (await api.put(`/imports/${importId}/mapping`, { format, mapping })).data;
export const previewImport = async (importId) => (await api.post(`/imports/${importId}/preview`)).data;
export const startImport = async (importId) => (await api.post(`/imports/${importId}/start`)).data;
export const getImport = async (importId) => (await api.get(`/imports/${importId}`)).data;
export const cancelImport = async (importId) => (await api.post(`/imports/${importId}/cancel`)).data;
export const importRows = async (importId, params) => (await api.get(`/imports/${importId}/rows`, { params })).data;
export const listImports = async (inventoryId) => (await api.get(`/inventories/${inventoryId}/imports`)).data;
export const failedCsvUrl = (importId) => `${apiBase()}/imports/${importId}/failed.csv`;

export const getJob = async (jobId) => (await api.get(`/jobs/${jobId}`)).data;
export const cancelJob = async (jobId) => (await api.post(`/jobs/${jobId}/cancel`)).data;
export const workerStatus = async () => (await api.get('/jobs/worker')).data;
export const fillFromSources = async (typeId) => (await api.post(`/item-types/${typeId}/fill-from-sources`)).data;
/** A scanning pile sent to the background (§5.6): entries [{code, source?, source_id?, values?, location_id?}]. */
export const addPileInBackground = async (inventoryId, typeId, entries, lang) => (await api.post(`/inventories/${inventoryId}/imports`, {
  item_type_id: typeId, format: 'codes', entries, lang,
})).data;

/**
 * The address of the CSV export: the list's own parameters (what the member
 * sees) or none (the whole collection), and the file's options.
 */
export const exportUrl = (inventoryId, listParams, { bom = true, codes = false, delimiter = '' } = {}) => {
  const params = new URLSearchParams(listParams || '');
  params.delete('page');
  params.delete('view');
  if (!bom) params.set('bom', '0');
  if (codes) params.set('codes', '1');
  if (delimiter) params.set('delimiter', delimiter);
  const query = params.toString();
  return `${apiBase()}/inventories/${inventoryId}/export.csv${query ? `?${query}` : ''}`;
};
